import { Injectable } from '@angular/core';
import { deepEqual, getCurrentTime } from 'src/app/common/app.helpers';
import { AggregatedStorageItem, MapState, ProcessMapResponseContent } from 'src/app/interfaces/map.interface';
import { Coords } from 'src/app/swagger-api/model/coords';
import { MapFilters } from './map.enum';
import {
  getTabRelatedMapState,
  hasIndividualMarkers,
  isInAggregationCountryZoomRange,
  isInAggregationRegionZoomRange,
  isInAggregationSubregionZoomRange,
  isInContinentCountryZoomRange,
} from './map.helpers';
import { MapService } from './map.service';
import MapSettings from './map.settings';
import { MapValues } from './map.values';

@Injectable()
export class ProxyMapService extends MapService {
  private centerCoordsMemory: Coords[] = [];
  private aggregatedItemsStorage: Map<string, AggregatedStorageItem> = new Map();

  public override onFilterChanged(filter: MapFilters): void {
    this.centerCoordsMemory = [this.currentState.location.center];

    super.onFilterChanged(filter);
  }

  public override updateCurrentMapState(state: MapState): void {
    super.updateCurrentMapState(state);

    if (this.currentState.form_location || this.previousState.form_location) {
      this.centerCoordsMemory = [this.currentState.location.center];
    } else {
      this.centerCoordsMemory.push(this.currentState.location.center);
    }
  }

  protected override fetchMarkers(currentState: MapState, previousState: MapState): void {
    if (!this.areStatesEqual(currentState, previousState)) {
      this.centerCoordsMemory = [currentState.location.center];

      currentState.filter === MapFilters.COVID
        ? this.fetchCovidMapData(currentState, previousState)
        : this.search(currentState, previousState);
      return;
    }

    const markerStatusChanges = { markers: this.mapMarkers, reset: false };
    this.markersChanges$.next(markerStatusChanges);
  }

  /**
   * @description Depend on current map state restores markers from the storage or
   *              re-fetches it by calling the parent map service's method.
   * @param { MapState } currentState
   * @param { MapState } previousState
   */
  protected override fetchAggregatedItems(currentState: MapState, previousState: MapState): void {
    if (
      this.isSearchFormSubmitted(currentState) &&
      !ProxyMapService.isSubmittedFormContentChanged(currentState, previousState)
    ) {
      return;
    }

    this.fetchFromStorage(currentState, previousState);
  }

  /**
   * @description Calls the parent map service's method and additionally saves incoming
   *              aggregated markers to the storage in case falsy submitted custom form.
   * @param { ProcessMapResponseContent } content
   * @returns { string }
   */
  protected override processSearchResponse(content: ProcessMapResponseContent): void {
    super.processSearchResponse(content);

    if (this.aggregatedSearchActivated && content.state.currentState.filter !== MapFilters.COVID) {
      this.aggregatedItemsStorage.set(this.getStorageKey(content.state.currentState), {
        lastFetchTime: getCurrentTime(),
        markers: this.mapMarkers,
        content,
      });
    }
  }

  /**
   * @description Returns a key combined from state zoom level, tab and filter.
   * @returns { string }
   * @param state MapState
   */
  private getStorageKey(state: MapState = this.currentState): string {
    const aggregationZoomLevel: number = this.getAggregationZoomLevel(state.zoom);
    const mapTab: MapFilters = state.filter;
    const mapFilter: string = this.getMapFilters(state);

    return `${aggregationZoomLevel}_${mapTab}${mapFilter}`;
  }

  /**
   * @description Returns a aggregation zoom level corresponding to current zoom level.
   * @returns { number }
   * @param zoom number
   */
  private getAggregationZoomLevel(zoom: number): number {
    if (isInAggregationCountryZoomRange(zoom)) {
      return MapSettings.MAP_SEARCH_COUNTRY_ZOOM_MIN_LEVEL;
    } else if (isInAggregationRegionZoomRange(zoom)) {
      return MapSettings.MAP_SEARCH_REGION_ZOOM_MIN_LEVEL;
    } else if (isInAggregationSubregionZoomRange(zoom)) {
      return MapSettings.MAP_SEARCH_SUBREGION_ZOOM_MIN_LEVEL;
    } else if (isInContinentCountryZoomRange(zoom)) {
      return MapSettings.MAP_SEARCH_CONTINENT_ZOOM_MIN_LEVEL;
    }
    return zoom;
  }

  /**
   * @description Returns combined key from SearchBlockValuesAndKeys.
   * @returns { string }
   * @param state MapState
   */
  private getMapFilters(state: MapState): string {
    const activatedFilters: string[] = [];
    Object.keys(state).forEach((key: string) => {
      if (MapValues.mapSearchBlockFormKeys.includes(key)) {
        const value: string | string[] = state[key] as string | string[];
        if (value && value.length !== 0) {
          activatedFilters.push(`${key}:${value.toString()}`);
        }
      }
    });

    if (activatedFilters.length > 0) {
      activatedFilters.sort();
      return `_${activatedFilters.join(';')}`;
    }

    return '';
  }

  /**
   * @description Returns result of check if passed aggregated markers were saved to
   *              storage more than 3 minutes ago.
   * @param { AggregatedStorageItem } item
   * @returns { boolean }
   */
  private static wasNoRefreshTimeExceeded(item: AggregatedStorageItem): boolean {
    return getCurrentTime() - item.lastFetchTime >= MapSettings.REFETCH_AGGREGATED_MARKERS_INTERVAL;
  }

  /**
   * @description Checks whether were states changed or not step by steps.
   * @param { MapState } currentState
   * @param { MapState } previousState
   * @returns { boolean }
   */
  private areStatesEqual(currentState: MapState, previousState: MapState): boolean {
    if (currentState.zoom !== previousState.zoom) {
      return false;
    }

    const currentSubmitted: MapState = getTabRelatedMapState(currentState);
    const previousSubmitted: MapState = getTabRelatedMapState(previousState);
    const areEqual: boolean = deepEqual(currentSubmitted, previousSubmitted);

    if (!areEqual) {
      return false;
    }

    return !ProxyMapService.wereCenterCoordsChanged(this.centerCoordsMemory);
  }

  /**
   * @description Checks whether were states zoom level values or form contents changed or not.
   * @param { MapState } currentState
   * @param { MapState } previousState
   * @returns { boolean }
   */
  private static isSubmittedFormContentChanged(currentState: MapState, previousState: MapState): boolean {
    return (
      currentState.zoom !== previousState.zoom ||
      !deepEqual(getTabRelatedMapState(currentState), getTabRelatedMapState(previousState))
    );
  }

  /**
   * @description Checks whether the  distance between two centers of states is greater
   *              than allowed one or not. In case an empty list or a list with just one
   *              item returns true by default.
   * @param { Coords[] } list
   * @returns { boolean }
   */
  private static wereCenterCoordsChanged(list: Coords[]): boolean {
    if (list.length < 2) {
      return true;
    }
    const firstCoords: Coords = list[0];
    const lastCoords: Coords = list.at(-1);

    const latDiff: number = Math.abs(firstCoords.lat - lastCoords.lat);
    const lonDiff: number = Math.abs(firstCoords.lon - lastCoords.lon);
    const significantChangeThreshold = 0.0001;

    return latDiff > significantChangeThreshold || lonDiff > significantChangeThreshold;
  }

  /**
   * @description Restores aggregated markers from the storage if it were found by key and
   *              updates it in case no refresh time exceeded. Otherwise, just re-fetches it.
   * @param { MapState } currentState
   * @param { MapState } previousState
   */
  private fetchFromStorage(currentState: MapState, previousState: MapState): void {
    const key: string = this.getStorageKey(currentState);
    if (this.aggregatedItemsStorage.has(key)) {
      const item: AggregatedStorageItem = this.aggregatedItemsStorage.get(key);

      if (hasIndividualMarkers(item.markers.allMarkers)) {
        this.processSearchResponse({
          ...item.content,
          ...{
            state: {
              currentState,
              previousState,
            },
          },
        });
      } else {
        this.emitMapMarkers(false, item.markers);
      }

      if (!ProxyMapService.wasNoRefreshTimeExceeded(item)) {
        return;
      }
    }

    super.fetchAggregatedItems(currentState, previousState);
  }
}
